import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Multiselect,
  Select,
  Spinner,
  Header,
  Alert,
  Tabs
} from "@amzn/awsui-components-react";

const CreateTeamModal = ({
  visible,
  onDismiss,
  onCreateTeam,
  onUpdateTeam,
  isPortalAdmin,
  isSupportAdmin,
  modalLoading,
  errorText,
  newTeamName,
  setNewTeamName,
  newTeamLDAP,
  setNewTeamLDAP,
  selectedOrgs,
  setSelectedOrgs,
  selectedRegions,
  setSelectedRegions,
  selectedVP,
  setSelectedVP,
  organizations,
  regions,
  directReports,
  getDirectReportsCaller,
  availableTeams,
  selectedTeamToEdit,
  setSelectedTeamToEdit,
  isAteamBI,
  onLoadTeamDetails,
  onClearError
}) => {
  const [activeTabId, setActiveTabId] = useState("create");
  const prevTabId = useRef(null);

  useEffect(() => {
    if (prevTabId.current !== null && prevTabId.current !== activeTabId) {
      console.log(`Tab switched from ${prevTabId.current} to ${activeTabId}, clearing fields`);
      
      setNewTeamName('');
      setNewTeamLDAP('');
      setSelectedOrgs([]);
      setSelectedRegions([]);
      setSelectedVP(null);
      setSelectedTeamToEdit(null);
      
      if (onClearError) {
        onClearError();
      }
    }
    
    prevTabId.current = activeTabId;
  }, [activeTabId, setNewTeamName, setNewTeamLDAP, setSelectedOrgs, setSelectedRegions, setSelectedVP, setSelectedTeamToEdit, onClearError]);

  const handleTeamSelection = (selectedOption) => {
    setSelectedTeamToEdit(selectedOption);
    if (selectedOption && onLoadTeamDetails) {
      onLoadTeamDetails(selectedOption.value);
    }
  };

  const renderTeamForm = () => (
    <>
      <FormField label={<>Team Display name <span style={{ color: 'red' }}>*</span></>}>
        <Input 
          onChange={({ detail }) => setNewTeamName(detail.value)}
          value={newTeamName}
          placeholder='Team name'
          required
        />
      </FormField>

      <FormField label={<>LDAP <span style={{ color: 'red' }}>*</span></>}>
        <Input 
          onChange={({ detail }) => setNewTeamLDAP(detail.value)}
          value={newTeamLDAP}
          placeholder='LDAP group'
          required
        />
      </FormField>

      <FormField label={<>Organization <span style={{ color: 'red' }}>*</span></>}>
        <Multiselect
          selectedOptions={selectedOrgs}
          onChange={({ detail }) => setSelectedOrgs(detail.selectedOptions)}
          options={organizations}
          placeholder="Choose Organizations"
          required
        />
      </FormField>

      <FormField label={<>Region <span style={{ color: 'red' }}>*</span></>}>
        <Multiselect
          selectedOptions={selectedRegions}
          onChange={({ detail }) => setSelectedRegions(detail.selectedOptions)}
          options={regions}
          placeholder="Choose Regions"
          required
        />
      </FormField>

      <FormField label={<>VP <span style={{ color: 'red' }}>*</span></>}>
        <Select
          selectedOption={selectedVP}
          onChange={({ detail }) => setSelectedVP(detail.selectedOption)}
          placeholder="Select a VP Alias"
          options={directReports}
          onLoadItems={() => {
            getDirectReportsCaller('shelleyr')
          }}
          required
        />
      </FormField>
    </>
  );

  const getButtonText = () => {
    return activeTabId === "create" ? "Create team" : "Update team";
  };

  const getHeaderText = () => {
    return activeTabId === "create" ? "Create a new team" : "Edit team details";
  };

  const handleSubmit = () => {
    if (activeTabId === "create") {
      onCreateTeam();
    } else {
      onUpdateTeam();
    }
  };

  const tabs = [
    {
      label: "Create Team",
      id: "create",
      content: (
        <>
          <Header variant="h3">
            <Box color="text-status-info">{getHeaderText()}</Box>
          </Header>
          {renderTeamForm()}
        </>
      )
    }
  ];

  if (isAteamBI) {
    tabs.push({
      label: "Edit Team",
      id: "edit",
      content: (
        <>
          <Header variant="h3">
            <Box color="text-status-info">{getHeaderText()}</Box>
          </Header>
          
          <FormField label={<>Select team to edit <span style={{ color: 'red' }}>*</span></>}>
            <Select
              selectedOption={selectedTeamToEdit}
              onChange={({ detail }) => handleTeamSelection(detail.selectedOption)}
              options={availableTeams}
              placeholder="Choose a team to edit"
              filteringType="auto"
              required
            />
          </FormField>

          {selectedTeamToEdit && renderTeamForm()}
        </>
      )
    });
  }

  return (
    <Modal
      onDismiss={onDismiss}
      visible={visible}
      footer={
        <>
          {errorText && (
            <Alert type="error" statusIconAriaLabel="Error">
              {errorText}
            </Alert>
          )}
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onDismiss}>Cancel</Button>
              <Button 
                variant="primary" 
                onClick={handleSubmit}
                disabled={
                  !(isPortalAdmin || isSupportAdmin) || 
                  (activeTabId === "edit" && !selectedTeamToEdit)
                }
              >
                {getButtonText()}
              </Button>
            </SpaceBetween>
          </Box>
        </>
      }
      header="Team Management"
    >
      {modalLoading ? <Spinner /> : (
        <Tabs
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
        />
      )}
    </Modal>
  );
};

export default CreateTeamModal;
